import Tippy from '@tippyjs/react';

import { ia, iaRa } from 'lib/helpers/utility';

import OverflowChipList from 'components/practice/BeyondBilling/PatientLedger/components/OverflowChipList/OverflowChipList';
import Button from 'components/shared/Button/Button';

const getBaseColumns = ({ showingRenderingBillingProviders }) =>
  [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      valueGetter: (params) => {
        const provider = params.data;
        return provider?.type === 'billing'
          ? provider?.bill_name
          : provider?.fullName || provider?.label;
      }
    },
    {
      field: 'npi',
      headerName: 'NPI',
      maxWidth: 120,
      flex: 1,
      valueGetter: (params) => {
        const provider = params.data;
        return provider?.type === 'billing'
          ? provider?.bill_npi
          : provider?.billingInfo?.npi || '-';
      }
    },
    !showingRenderingBillingProviders && {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      maxWidth: 160,
      valueGetter: (params) => {
        const provider = params.data;
        return provider?.type === 'billing' ? 'Billing provider' : 'Rendering provider';
      }
    }
  ].filter(Boolean);

const renderStatesCell = (params, stateOptions, onOpenStatesModal) => {
  const selectedIds = iaRa(params.data?.state_ids);
  const labelById = new Map(stateOptions.map((opt) => [opt.value, opt.label]));
  const selectedLabels = selectedIds.map((id) => labelById.get(id)).filter(Boolean);

  return (
    <div className="flex w-full items-center justify-between gap-2">
      {!ia(selectedLabels) ? (
        <Button
          style="outlined"
          color="neutral"
          className="px-[10px] py-[5px]"
          textClassName="text-neutral-700"
          size="sm"
          text="Add"
          icon="add-square-mono"
          onClick={() => onOpenStatesModal?.(params.data)}
        />
      ) : (
        <>
          <OverflowChipList
            items={selectedLabels}
            emptyText="No states selected"
            justify="start"
            visibleCount={2}
          />
          <Tippy content="Edit states" placement="bottom" className="tippy-dark">
            <Button
              variant="link"
              color="primary"
              size="sm"
              icon="edit-3-mono"
              iconColor="neutral"
              aria-label="Edit states"
              onClick={() => onOpenStatesModal?.(params.data)}
            />
          </Tippy>
        </>
      )}
    </div>
  );
};

export const getProviderColumns = ({
  enableSelection,
  showingRenderingBillingProviders,
  showStates,
  onRemove,
  stateOptions,
  onOpenStatesModal
}) => {
  const baseColumns = getBaseColumns({ showingRenderingBillingProviders });

  const statesColumn = showStates && {
    field: 'state_ids',
    headerName: 'States',
    flex: 2,
    minWidth: 200,
    sortable: false,
    filter: false,
    autoHeight: true,
    cellStyle: { display: 'flex', alignItems: 'center', paddingTop: 4, paddingBottom: 4 },
    cellRenderer: (params) => renderStatesCell(params, stateOptions, onOpenStatesModal)
  };

  if (onRemove && !enableSelection) {
    return [
      {
        maxWidth: 50,
        flex: 0,
        cellRenderer: (params) => (
          <Button
            variant="link"
            color="neutral"
            icon="close-mono"
            onClick={() => onRemove(params.data?.value ?? params.data?.id)}
          />
        ),
        sortable: false,
        filter: false,
        resizable: false
      },
      ...baseColumns,
      ...(statesColumn ? [statesColumn] : [])
    ];
  }

  if (enableSelection) {
    return [
      {
        ...baseColumns[0],
        checkboxSelection: true,
        headerCheckboxSelection: true
      },
      ...baseColumns.slice(1)
    ];
  }

  return [...baseColumns, ...(statesColumn ? [statesColumn] : [])];
};
